/**
 * ProductWizardPage - conversational AI for structuring product data.
 *
 * Uses the generic wizard components and the product-specific proposal
 * renderer. Creates or resumes a PRODUCT_WIZARD session on mount.
 *
 * Fills the remaining space below the Products tab bar.
 */

import { useEffect } from 'react'
import { useWizard } from '@/admin/hooks/use-wizard'
import { WizardChat } from '@/admin/components/wizard/wizard-chat'
import { WizardInput } from '@/admin/components/wizard/wizard-input'
import { WizardProposal } from '@/admin/components/wizard/wizard-proposal'
import { WizardLayout } from '@/admin/components/wizard/wizard-layout'
import { ProductProposalView } from '@/admin/pages/manage/components/product-proposal-view'

export function ProductWizardPage() {
  const wizard = useWizard('PRODUCT_WIZARD')

  // Create or resume session on mount
  useEffect(() => {
    if (!wizard.sessionUuid) {
      wizard.createSession()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const messages = wizard.session?.messages ?? []
  const proposal = wizard.session?.proposal ?? null
  const hasProposal = proposal !== null && wizard.session?.status === 'PROPOSAL_PENDING'

  return (
    <div className="mx-auto mt-4 w-full max-w-[1200px] max-h-[calc(100vh-56px-40px-48px-40px-24px)] rounded-[8px] border-[0.5px] border-[var(--rule)]">
      <WizardLayout
        chat={
          <WizardChat
            messages={messages}
            isStreaming={wizard.isStreaming}
            streamingText={wizard.streamingText}
          />
        }
        input={
          <WizardInput
            onSend={wizard.sendMessage}
            onUploadFile={wizard.uploadFile}
            disabled={wizard.isStreaming || wizard.isAccepting}
            isUploading={wizard.isUploading}
          />
        }
        proposal={
          hasProposal ? (
            <WizardProposal
              proposal={proposal}
              onAccept={() => wizard.acceptProposal()}
              onReject={() => wizard.rejectProposal()}
              isAccepting={wizard.isAccepting}
              isRejecting={wizard.isRejecting}
              renderProposal={(p) => <ProductProposalView proposal={p} />}
            />
          ) : undefined
        }
      />
    </div>
  )
}
